import isFunction from "lodash/isFunction";
import React from "react";

import { Impulse } from "~/tools/impulse";
import { usePermanent } from "~/tools/use-permanent";

import { Widget, WidgetBody, WidgetHeader, type WidgetHeaderProps, type WidgetNamespacedProps } from "./_widget";

type HeaderRenderer = (props: Pick<WidgetHeaderProps, "title" | "help" | "size">) => React.ReactNode;

export interface OverviewWidgetProps extends Pick<WidgetNamespacedProps, "namespace" | "py"> {
	className?: string;

	layout: "full" | "compact" | "plain";

	title: React.ReactNode;

	/**
	 * Only visible in full layout
	 */
	help?: string;

	/**
	 * @default true
	 */
	isExpandedByDefault?: boolean;

	/**
	 * Content of the WidgetHeader
	 */
	header?: React.ReactNode | HeaderRenderer;
}

export const OverviewWidget: React.FC<React.PropsWithChildren<OverviewWidgetProps>> = ({
	layout,
	title,
	help,
	isExpandedByDefault = true,
	header,
	children,
	...props
}) => {
	const isSectionExpanded = usePermanent(() => Impulse<boolean>());
	const headerProps = {
		title,
		help: layout === "full" ? help : undefined,
		size: layout === "full" ? "lg" : "md",
	} satisfies WidgetHeaderProps;

	React.useEffect(() => {
		if (isExpandedByDefault) {
			isSectionExpanded.setValue((expanded) => {
				if (expanded == null) {
					return undefined;
				}

				return true;
			});
		}
	}, [isSectionExpanded, isExpandedByDefault]);

	return (
		<Widget
			card={layout === "full"}
			isExpanded={isSectionExpanded}
			isExpandedByDefault={isExpandedByDefault}
			{...props}
		>
			{isFunction(header) ? header(headerProps) : <WidgetHeader {...headerProps}>{header}</WidgetHeader>}

			<WidgetBody card={layout === "compact"}>{children}</WidgetBody>
		</Widget>
	);
};
